import React, { useState } from 'react';
import { ref, push, set, serverTimestamp } from 'firebase/database';
import { database } from '../firebase';
import './MembershipForm.css';

const INITIAL_STATE = {
  membershipType: '',
  applicationCity: '',
  applicationDistrict: '',
  applicationDate: new Date().toISOString().slice(0, 10),
  fullName: '',
  tcNo: '',
  birthDate: '',
  birthPlace: '',
  gender: '',
  phone: '',
  email: '',
  address: '',
  university: '',
  faculty: '',
  graduationYear: '',
  academicTitle: '',
  company: '',
  position: '',
  experienceYears: '',
  sector: '',
  expertise: '',
  kysExperience: '',
  auditExperience: '',
  processImprovementExperience: '',
  certifications: '',
  cvUrl: '',
  reference1: '',
  reference2: '',
  declarationAccepted: false,
  contactAccepted: false,
  kvkkAccepted: false,
};

const EMPTY_CERT = { name: '', url: '' };
const EMPTY_TRAINING = { name: '', url: '' };

const MembershipForm = ({ onBack }) => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [certList, setCertList] = useState([{ ...EMPTY_CERT }]);
  const [trainingList, setTrainingList] = useState([{ ...EMPTY_TRAINING }]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [error, setError] = useState('');

  const update = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.declarationAccepted || !form.contactAccepted || !form.kvkkAccepted) {
      setError('Lütfen tüm beyan ve onay kutucuklarını işaretleyiniz.');
      return;
    }
    if (!form.fullName || !form.email || !form.phone) {
      setError('Lütfen zorunlu alanları (ad soyad, e-posta, telefon) doldurunuz.');
      return;
    }
    setSubmitting(true);
    try {
      const applicationRef = ref(database, 'membershipApplications');
      const newRef = push(applicationRef);
      const applicationId = newRef.key;

      const payload = {
        ...form,
        certifications: certList.filter((c) => c.name.trim()),
        trainings: trainingList.filter((t) => t.name.trim()),
        createdAt: serverTimestamp(),
      };
      await set(ref(database, `membershipApplications/${applicationId}`), payload);
      setApplicationId(applicationId);
      setSuccess(true);
      setForm(INITIAL_STATE);
      setCertList([{ ...EMPTY_CERT }]);
      setTrainingList([{ ...EMPTY_TRAINING }]);
    } catch (err) {
      setError(err.message || 'Başvuru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    const handleCopyId = () => {
      if (applicationId) {
        navigator.clipboard.writeText(applicationId).then(() => alert('Başvuru numarası kopyalandı.'));
      }
    };
    return (
      <div className="membership-form-page">
        <div className="membership-form-hero">
          <div className="container">
            <h1 className="membership-form-hero-title">Başvurunuz Alındı</h1>
            <p className="membership-form-hero-sub">
              Online üyelik başvurunuz başarıyla iletilmiştir. En kısa sürede değerlendirilip tarafınıza dönüş yapılacaktır.
            </p>
            {applicationId && (
              <div className="membership-form-application-id">
                <p className="application-id-label">Başvuru numaranız (bu numarayı kaybetmeyiniz):</p>
                <div className="application-id-box">
                  <code className="application-id-value">{applicationId}</code>
                  <button type="button" className="application-id-copy" onClick={handleCopyId}>
                    Kopyala
                  </button>
                </div>
                <p className="application-id-note">
                  Başvuru durumunuzu öğrenmek için sitedeki &quot;Başvuru Sorgula&quot; bölümünden bu numarayı girebilirsiniz. Lütfen bu numarayı not alın veya güvenli bir yerde saklayın.
                </p>
              </div>
            )}
            {onBack && (
              <button type="button" className="membership-form-back-btn" onClick={onBack}>
                Ana sayfaya dön
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="membership-form-page">
      <div className="membership-form-hero">
        <div className="container">
          <h1 className="membership-form-hero-title">Online Başvuru Formu</h1>
          <p className="membership-form-hero-sub">TKMB – Türkiye Kalite Mühendisleri Birliği</p>
        </div>
      </div>

      <div className="membership-form-cert-section">
        <div className="container">
          <div className="membership-form-cert-inner">
            <img
              src="/meslekiyetkinlikbelgesi.jpeg"
              alt="Mesleki Yetkinlik Belgesi"
              className="membership-form-cert-img"
            />
            <div className="membership-form-cert-text">
              <h2 className="membership-form-cert-title">Yetkinlik belgenizi alarak sicil numaranızı oluşturun</h2>
              <p className="membership-form-cert-desc">
                Aşağıdaki formu doldurarak TKMB Mesleki Yetkinlik Belgesi başvurusunda bulunabilir ve kişisel sicil numaranızı oluşturabilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </div>

      <form className="membership-form-content" onSubmit={handleSubmit}>
        <div className="container">
          {error && <div className="membership-form-error">{error}</div>}

          {/* 1) Başvuru Bilgileri */}
          <section className="form-section">
            <h2 className="form-section-title">1) Başvuru Bilgileri</h2>
            <div className="form-grid-2">
              <div className="form-field">
                <label>Başvuru İl</label>
                <input type="text" value={form.applicationCity} onChange={(e) => update('applicationCity', e.target.value)} placeholder="İl" />
              </div>
              <div className="form-field">
                <label>Başvuru İlçe</label>
                <input type="text" value={form.applicationDistrict} onChange={(e) => update('applicationDistrict', e.target.value)} placeholder="İlçe" />
              </div>
            </div>
            <div className="form-field">
              <label>Başvuru Tarihi</label>
              <input type="date" value={form.applicationDate} onChange={(e) => update('applicationDate', e.target.value)} />
            </div>
          </section>

          {/* 2) Kişisel Bilgiler */}
          <section className="form-section">
            <h2 className="form-section-title">2) Kişisel Bilgiler</h2>
            <div className="form-field">
              <label>Ad Soyad <span className="required">*</span></label>
              <input type="text" value={form.fullName} onChange={(e) => update('fullName', e.target.value)} required placeholder="Ad Soyad" />
            </div>
            <div className="form-grid-2">
              <div className="form-field">
                <label>T.C. Kimlik No</label>
                <input type="text" value={form.tcNo} onChange={(e) => update('tcNo', e.target.value)} placeholder="11 haneli" maxLength={11} />
              </div>
              <div className="form-field">
                <label>Doğum Tarihi</label>
                <input type="date" value={form.birthDate} onChange={(e) => update('birthDate', e.target.value)} />
              </div>
            </div>
            <div className="form-field">
              <label>Doğum Yeri</label>
              <input type="text" value={form.birthPlace} onChange={(e) => update('birthPlace', e.target.value)} placeholder="İl / İlçe" />
            </div>
            <div className="form-row">
              <label className="form-label">Cinsiyet</label>
              <div className="form-radio-group">
                {['Kadın', 'Erkek', 'Belirtmek istemiyorum'].map((opt) => (
                  <label key={opt} className="form-radio">
                    <input type="radio" name="gender" value={opt} checked={form.gender === opt} onChange={(e) => update('gender', e.target.value)} />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* 3) İletişim Bilgileri */}
          <section className="form-section">
            <h2 className="form-section-title">3) İletişim Bilgileri</h2>
            <div className="form-grid-2">
              <div className="form-field">
                <label>Cep Telefonu <span className="required">*</span></label>
                <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} required placeholder="05XX XXX XX XX" />
              </div>
              <div className="form-field">
                <label>E-posta <span className="required">*</span></label>
                <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required placeholder="ornek@email.com" />
              </div>
            </div>
            <div className="form-field">
              <label>Adres</label>
              <textarea value={form.address} onChange={(e) => update('address', e.target.value)} rows={3} placeholder="Açık adres" />
            </div>
          </section>

          {/* 4) Eğitim Bilgileri */}
          <section className="form-section">
            <h2 className="form-section-title">4) Eğitim Bilgileri</h2>
            <div className="form-field">
              <label>Üniversite</label>
              <input type="text" value={form.university} onChange={(e) => update('university', e.target.value)} placeholder="Üniversite adı" />
            </div>
            <div className="form-field">
              <label>Fakülte / Bölüm</label>
              <input type="text" value={form.faculty} onChange={(e) => update('faculty', e.target.value)} placeholder="Fakülte ve bölüm" />
            </div>
            <div className="form-grid-2">
              <div className="form-field">
                <label>Mezuniyet Yılı</label>
                <input type="text" value={form.graduationYear} onChange={(e) => update('graduationYear', e.target.value)} placeholder="Örn: 2010" />
              </div>
              <div className="form-field">
                <label>Akademik Unvan (varsa)</label>
                <input type="text" value={form.academicTitle} onChange={(e) => update('academicTitle', e.target.value)} placeholder="Dr., Doç. Dr. vb." />
              </div>
            </div>
          </section>

          {/* 5) Mesleki Bilgiler */}
          <section className="form-section">
            <h2 className="form-section-title">5) Mesleki Bilgiler</h2>
            <div className="form-field">
              <label>Çalıştığı Kurum / Şirket</label>
              <input type="text" value={form.company} onChange={(e) => update('company', e.target.value)} placeholder="Kurum adı" />
            </div>
            <div className="form-grid-2">
              <div className="form-field">
                <label>Görev / Unvan</label>
                <input type="text" value={form.position} onChange={(e) => update('position', e.target.value)} placeholder="Görev unvanı" />
              </div>
              <div className="form-field">
                <label>Toplam Tecrübe (yıl)</label>
                <input type="text" value={form.experienceYears} onChange={(e) => update('experienceYears', e.target.value)} placeholder="Yıl" />
              </div>
            </div>
            <div className="form-grid-2">
              <div className="form-field">
                <label>Sektör</label>
                <input type="text" value={form.sector} onChange={(e) => update('sector', e.target.value)} placeholder="Sektör" />
              </div>
              <div className="form-field">
                <label>Uzmanlık Alanı</label>
                <input type="text" value={form.expertise} onChange={(e) => update('expertise', e.target.value)} placeholder="Uzmanlık alanı" />
              </div>
            </div>
          </section>

          {/* 6) Yetkinlik ve Deneyim */}
          <section className="form-section">
            <h2 className="form-section-title">6) Yetkinlik ve Deneyim Bilgileri</h2>
            <div className="form-field">
              <label>Kalite Yönetim Sistemleri Deneyimi</label>
              <textarea value={form.kysExperience} onChange={(e) => update('kysExperience', e.target.value)} rows={3} placeholder="Kısaca belirtiniz" />
            </div>
            <div className="form-field">
              <label>Denetim Tecrübesi</label>
              <textarea value={form.auditExperience} onChange={(e) => update('auditExperience', e.target.value)} rows={3} placeholder="İç/dış denetim tecrübesi" />
            </div>
            <div className="form-field">
              <label>Süreç İyileştirme / Problem Çözme Deneyimi</label>
              <textarea value={form.processImprovementExperience} onChange={(e) => update('processImprovementExperience', e.target.value)} rows={3} placeholder="Kısaca belirtiniz" />
            </div>
          </section>

          {/* 7) Sertifikalar */}
          <section className="form-section">
            <h2 className="form-section-title">7) Sertifikalar ve Eğitimler</h2>

            <div className="form-field">
              <label>Sertifikalar</label>
              {certList.map((cert, i) => (
                <div key={i} className="form-link-row">
                  <input
                    type="text"
                    placeholder="Sertifika adı (ISO 9001, 6 Sigma vb.)"
                    value={cert.name}
                    onChange={(e) => {
                      const next = [...certList];
                      next[i] = { ...next[i], name: e.target.value };
                      setCertList(next);
                    }}
                  />
                  <input
                    type="url"
                    placeholder="Sertifika linki (opsiyonel)"
                    value={cert.url}
                    onChange={(e) => {
                      const next = [...certList];
                      next[i] = { ...next[i], url: e.target.value };
                      setCertList(next);
                    }}
                  />
                  {certList.length > 1 && (
                    <button type="button" className="form-link-remove" onClick={() => setCertList(certList.filter((_, j) => j !== i))}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="form-link-add" onClick={() => setCertList([...certList, { ...EMPTY_CERT }])}>+ Sertifika Ekle</button>
            </div>

            <div className="form-field">
              <label>Eğitimler</label>
              {trainingList.map((tr, i) => (
                <div key={i} className="form-link-row">
                  <input
                    type="text"
                    placeholder="Eğitim adı"
                    value={tr.name}
                    onChange={(e) => {
                      const next = [...trainingList];
                      next[i] = { ...next[i], name: e.target.value };
                      setTrainingList(next);
                    }}
                  />
                  <input
                    type="url"
                    placeholder="Eğitim linki (opsiyonel)"
                    value={tr.url}
                    onChange={(e) => {
                      const next = [...trainingList];
                      next[i] = { ...next[i], url: e.target.value };
                      setTrainingList(next);
                    }}
                  />
                  {trainingList.length > 1 && (
                    <button type="button" className="form-link-remove" onClick={() => setTrainingList(trainingList.filter((_, j) => j !== i))}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="form-link-add" onClick={() => setTrainingList([...trainingList, { ...EMPTY_TRAINING }])}>+ Eğitim Ekle</button>
            </div>

            <div className="form-field">
              <label>CV Linki</label>
              <input
                type="url"
                value={form.cvUrl}
                onChange={(e) => update('cvUrl', e.target.value)}
                placeholder="https://drive.google.com/..."
              />
              <span className="form-field-hint">CV'nizi Google Drive'a yükleyip paylaşılabilir linkini yapıştırabilirsiniz.</span>
            </div>
          </section>

          {/* 8) Referans */}
          <section className="form-section">
            <h2 className="form-section-title">8) Referans Bilgileri (opsiyonel)</h2>
            <div className="form-field">
              <label>Referans 1</label>
              <input type="text" value={form.reference1} onChange={(e) => update('reference1', e.target.value)} placeholder="Ad, kurum, iletişim" />
            </div>
            <div className="form-field">
              <label>Referans 2</label>
              <input type="text" value={form.reference2} onChange={(e) => update('reference2', e.target.value)} placeholder="Ad, kurum, iletişim" />
            </div>
          </section>

          {/* 9) Beyan ve Onay */}
          <section className="form-section form-section-declaration">
            <h2 className="form-section-title">9) Beyan ve Onay</h2>
            <div className="form-checkboxes">
              <label className="form-checkbox">
                <input type="checkbox" checked={form.declarationAccepted} onChange={(e) => update('declarationAccepted', e.target.checked)} />
                <span>Verdiğim bilgilerin doğru olduğunu beyan ederim. <span className="required">*</span></span>
              </label>
              <label className="form-checkbox">
                <input type="checkbox" checked={form.contactAccepted} onChange={(e) => update('contactAccepted', e.target.checked)} />
                <span>Başvuru süreci kapsamında iletişime geçilmesini kabul ederim. <span className="required">*</span></span>
              </label>
              <label className="form-checkbox">
                <input type="checkbox" checked={form.kvkkAccepted} onChange={(e) => update('kvkkAccepted', e.target.checked)} />
                <span>Kişisel verilerimin işlenmesine ilişkin bilgilendirmeyi kabul ediyorum. <span className="required">*</span></span>
              </label>
            </div>
          </section>

          <div className="form-actions">
            {onBack && (
              <button type="button" className="form-btn form-btn-secondary" onClick={onBack}>
                İptal
              </button>
            )}
            <button type="submit" className="form-btn form-btn-primary" disabled={submitting}>
              {submitting ? 'Gönderiliyor…' : 'Başvuruyu Gönder'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MembershipForm;
